"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/PlaidConnectFlow/hooks/usePlaidManualUpload";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { OspToast } from "@ospweb/sdk-ui";
import { Track } from "@/src/lib/tracking";
import {
  uploadAttachmentFiles,
  type UploadedAttachmentFile,
} from "@/src/utils/attachmentUpload/uploadAttachmentFiles";
import { resolvePortfolioUploadAuthHeaders } from "@/src/hooks/onboarding/portfolio/upload/resolveUploadAuthHeaders";
import type { SenderAttachment } from "@components/Sender/type";
import { rootStore } from "@store/rootStore";
import type { PlaidUploadItem } from "../shared/PlaidUploadedDocumentChip";
import type { PlaidUploadEntry, PlaidUploadFrom } from "../types";
import { createImagePreview, isImageFile } from "../utils/createImagePreview";
import log from "logbeacon";

const I18n = I18nUtil.createScoped(Translations);

export const PLAID_MAX_MANUAL_FILES = 10;
export const PLAID_MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB
/** 「Try again」单次重传的超时：超时按失败处理（EDG-561 AC-3.7） */
export const PLAID_RETRY_TIMEOUT_MS = 15_000;
/** 重叠链路的超限提示停留时长（Figma SC-06 toast） */
const OVERLAP_LIMIT_TOAST_MS = 3000;

const ALLOWED_EXTENSIONS = [
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "webp",
  "csv",
  "xlsx",
  "xls",
];

const ALLOWED_MIME_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

function isFileTypeSupported(file: File): boolean {
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  if (ALLOWED_EXTENSIONS.includes(ext)) {
    return true;
  }
  if (file.type && ALLOWED_MIME_TYPES.includes(file.type)) {
    return true;
  }
  return false;
}

export interface UsePlaidManualUploadOptions {
  /** 来自重叠分析链路；缺省 = 线上 V3.0.6 行为（文案 / toast / 事件参数均不变） */
  entry?: PlaidUploadEntry;
  from?: PlaidUploadFrom;
}

export interface UsePlaidManualUploadReturn {
  rawFiles: PlaidUploadItem[];
  uploadedFiles: SenderAttachment[];
  isUploading: boolean;
  /** 存在 `status:"error"` 的项（重叠链路据此把 Confirm 换成 Try again） */
  hasError: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  openFilePicker: () => void;
  handleFileInputChange: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<void>;
  handlePickFiles: (files: FileList | File[]) => Promise<boolean>;
  removeFile: (id: string) => void;
  clearFiles: () => void;
  /** 只重传失败项（用保留的 `file` 引用），全部成功返回 true；无失败项直接返回 true */
  retryFailed: () => Promise<boolean>;
}

/** 埋点公共参数：entry / from 缺省时不带，保证线上事件载荷不变 */
const attribution = (opts: UsePlaidManualUploadOptions) => ({
  ...(opts.entry ? { entry: opts.entry } : {}),
  ...(opts.from ? { from: opts.from } : {}),
});

export function usePlaidManualUpload(
  initialFiles: UploadedAttachmentFile[] = [],
  options: UsePlaidManualUploadOptions = {},
): UsePlaidManualUploadReturn {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { fingerprint } = useSnapshot(rootStore.user);
  const [items, setItems] = useState<PlaidUploadItem[]>(() =>
    initialFiles.map((file, idx) => ({
      ...file,
      id: `init_${idx}_${file.filename}`,
      status: "done",
    })),
  );
  const isOverlap = options.entry === "overlap";
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // 同步镜像：事件回调 / 卸载清理里要读最新列表，又不想把 items 放进依赖导致回调频繁重建
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const revokePreview = useCallback((url?: string) => {
    if (url?.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  }, []);

  // 卸载：释放所有本地预览（EDG-561 SC-08）
  useEffect(
    () => () => {
      itemsRef.current.forEach((item) => revokePreview(item.previewUrl));
    },
    [revokePreview],
  );

  const isUploading = useMemo(
    () => items.some((item) => item.status === "uploading"),
    [items],
  );
  const hasError = useMemo(
    () => items.some((item) => item.status === "error"),
    [items],
  );

  const uploadedFiles = useMemo(
    () =>
      items
        .filter((item) => item.status === "done" && Boolean(item.url))
        .map(({ filename, mediaType, url, size }) => ({
          type: "file" as const,
          filename,
          ...(mediaType ? { mediaType } : {}),
          url,
          ...(size != null ? { size } : {}),
        })),
    [items],
  );

  const openFilePicker = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  }, []);

  const removeFile = useCallback(
    (id: string) => {
      const removed = itemsRef.current.find((item) => item.id === id);
      if (!removed) {
        return;
      }
      Track.event("plaid_manual_upload_remove", {
        filename: removed.filename,
        ...attribution(optionsRef.current),
      });
      revokePreview(removed.previewUrl);
      setItems((prev) => prev.filter((item) => item.id !== id));
    },
    [revokePreview],
  );

  const clearFiles = useCallback(() => {
    itemsRef.current.forEach((item) => revokePreview(item.previewUrl));
    setItems([]);
  }, [revokePreview]);

  /** 异步生成图片的本地压缩预览；生成回来时若该项已被移除 / 组件已卸载，立即释放 */
  const attachPreviews = useCallback(
    (batch: PlaidUploadItem[], files: File[]) => {
      batch.forEach((item, idx) => {
        const file = files[idx];
        if (!file || !isImageFile(file)) {
          return;
        }
        void createImagePreview(file)
          .then((previewUrl) => {
            const alive = itemsRef.current.some((it) => it.id === item.id);
            if (!alive) {
              revokePreview(previewUrl);
              return;
            }
            setItems((prev) =>
              prev.map((it) =>
                it.id === item.id ? { ...it, previewUrl } : it,
              ),
            );
          })
          .catch(() => {
            // createImagePreview 自身已回退，走到这里只会是 createObjectURL 不可用；缩略图退化为类型图标
          });
      });
    },
    [revokePreview],
  );

  /**
   * 上传一批并把结果写回 items；返回是否全部成功。
   * `timeoutMs` 仅 retry 使用：超时视作失败，不让「Try again」无限转圈。
   */
  const uploadBatch = useCallback(
    async (
      batch: PlaidUploadItem[],
      files: File[],
      timeoutMs?: number,
    ): Promise<boolean> => {
      const attr = attribution(optionsRef.current);
      const overlap = optionsRef.current.entry === "overlap";
      const markFailed = (reason: "no_url" | "exception") => {
        Track.event("plaid_manual_upload_fail", {
          reason,
          count: batch.length,
          ...attr,
        });
        // 重叠链路的失败由上传页内联提示 + Try again 承载（SC-07），不再弹 toast
        if (!overlap) {
          OspToast.error({
            content: I18n.t("Upload failed. Please try again."),
          });
        }
      };

      try {
        const uploadAuthHeaders = await resolvePortfolioUploadAuthHeaders({});
        const upload = uploadAttachmentFiles({
          files,
          authHeaders: uploadAuthHeaders,
          fingerprint,
        });
        const uploadedResults = await (timeoutMs
          ? Promise.race([
              upload,
              new Promise<never>((_, reject) =>
                setTimeout(
                  () => reject(new Error("upload timeout")),
                  timeoutMs,
                ),
              ),
            ])
          : upload);

        const itemUpdates: Record<string, Partial<PlaidUploadItem>> = {};
        let batchHadFailure = false;
        batch.forEach((item, idx) => {
          const res = uploadedResults[idx];
          if (res?.url) {
            itemUpdates[item.id] = {
              filename: res.filename || item.filename,
              size: res.size ?? item.size,
              url: res.url,
              mediaType: res.mediaType || item.mediaType,
              status: "done",
            };
          } else {
            batchHadFailure = true;
            itemUpdates[item.id] = { status: "error" };
          }
        });

        setItems((prev) =>
          prev.map((item) =>
            itemUpdates[item.id] ? { ...item, ...itemUpdates[item.id] } : item,
          ),
        );

        if (batchHadFailure) {
          markFailed("no_url");
        }

        const successResults = uploadedResults.filter((r) => Boolean(r?.url));
        if (successResults.length > 0) {
          Track.event("plaid_manual_upload_success", {
            count: successResults.length,
            files: successResults.map((r) => r.filename).join(", "),
            ...attr,
          });
        }
        return !batchHadFailure;
      } catch (error) {
        log.error("[文案]Manual document upload failed:", error);
        markFailed("exception");
        const ids = new Set(batch.map((item) => item.id));
        setItems((prev) =>
          prev.map((item) =>
            ids.has(item.id) && item.status === "uploading"
              ? { ...item, status: "error" as const }
              : item,
          ),
        );
        return false;
      }
    },
    [fingerprint],
  );

  const retryFailed = useCallback(async (): Promise<boolean> => {
    const failed = itemsRef.current.filter(
      (item) => item.status === "error" && item.file,
    );
    if (failed.length === 0) {
      return !itemsRef.current.some((item) => item.status === "error");
    }
    const failedIds = new Set(failed.map((item) => item.id));
    setItems((prev) =>
      prev.map((item) =>
        failedIds.has(item.id)
          ? { ...item, status: "uploading" as const }
          : item,
      ),
    );
    const ok = await uploadBatch(
      failed,
      failed.map((item) => item.file as File),
      PLAID_RETRY_TIMEOUT_MS,
    );
    // 不读 itemsRef 判成败：setItems 的重渲染还没落地，镜像此刻是旧的；本批结果就是 `ok`
    const unretryable = itemsRef.current.some(
      (item) => item.status === "error" && !item.file,
    );
    return ok && !unretryable;
  }, [uploadBatch]);

  const handlePickFiles = useCallback(
    async (picked: FileList | File[]): Promise<boolean> => {
      const fileArray = Array.from(picked);
      if (fileArray.length === 0) {
        return false;
      }

      Track.event("plaid_manual_upload_pick", {
        count: fileArray.length,
        ...attribution(optionsRef.current),
      });

      // 1. Filter supported types
      const unsupported = fileArray.filter(
        (file) => !isFileTypeSupported(file),
      );
      if (unsupported.length > 0) {
        OspToast.error({
          content: I18n.t(
            "Only PDF, image (PNG/JPG/WEBP), or CSV/Excel files are supported.",
          ),
        });
      }
      let validFiles = fileArray.filter((file) => isFileTypeSupported(file));
      if (validFiles.length === 0) {
        return false;
      }

      // 2. Filter oversized files (<= 20MB)
      const oversized = validFiles.filter(
        (file) => file.size > PLAID_MAX_FILE_SIZE_BYTES,
      );
      if (oversized.length > 0) {
        OspToast.error({
          content: I18n.t("File size exceeds 20MB limit."),
        });
      }
      validFiles = validFiles.filter(
        (file) => file.size <= PLAID_MAX_FILE_SIZE_BYTES,
      );
      if (validFiles.length === 0) {
        return false;
      }

      // 3. Count limits (<= 10)
      const currentCount = itemsRef.current.length;
      const remaining = PLAID_MAX_MANUAL_FILES - currentCount;
      if (remaining <= 0) {
        if (isOverlap) {
          OspToast.info({
            content: I18n.t("10 files max. The extras could not be uploaded."),
            duration: OVERLAP_LIMIT_TOAST_MS,
          });
        } else {
          OspToast.error({
            content: I18n.t("You can upload up to %{var0} documents.", {
              var0: PLAID_MAX_MANUAL_FILES,
            }),
          });
        }
        return false;
      }
      if (validFiles.length > remaining) {
        if (isOverlap) {
          OspToast.info({
            content: I18n.t("10 files max. The extras could not be uploaded."),
            duration: OVERLAP_LIMIT_TOAST_MS,
          });
        } else {
          OspToast.info({
            content: I18n.t(
              "Only the first %{var0} document(s) were added due to limit.",
              {
                var0: remaining,
              },
            ),
          });
        }
        validFiles = validFiles.slice(0, remaining);
      }

      // 4. Create batch items with uploading state
      const batchId = `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const newItems: PlaidUploadItem[] = validFiles.map((file, idx) => ({
        id: `${batchId}_${idx}`,
        filename: file.name,
        size: file.size,
        url: "",
        status: "uploading",
        mediaType: file.type || undefined,
        file,
      }));

      // 同步写镜像：紧随其后的 attachPreviews / 并发的二次选择都要能立刻读到
      itemsRef.current = [...itemsRef.current, ...newItems];
      setItems((prev) => [...prev, ...newItems]);
      attachPreviews(newItems, validFiles);

      // 5. Upload via API
      return uploadBatch(newItems, validFiles);
    },
    [attachPreviews, isOverlap, uploadBatch],
  );

  const handleFileInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        await handlePickFiles(files);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [handlePickFiles],
  );

  return {
    rawFiles: items,
    uploadedFiles,
    isUploading,
    hasError,
    fileInputRef,
    openFilePicker,
    handleFileInputChange,
    handlePickFiles,
    removeFile,
    clearFiles,
    retryFailed,
  };
}
